import {
  apiCreatePartnerPopup,
  apiDeletePartnerPopup,
  apiGetPartnerPopup,
  apiGetPartnerPopups,
  apiUpdatePartnerPopup,
} from '@/api/partnerPopups';

export const REGIONS = [
  { value: 'CA', text: 'Canada' },
  { value: 'US', text: 'United States' },
];

export const ALL_REGIONS = REGIONS.map((region) => region.value);

export const STATUSES = [
  { value: 'draft', text: 'Draft' },
  { value: 'scheduled', text: 'Scheduled' },
  { value: 'live', text: 'Live' },
  { value: 'paused', text: 'Paused' },
  { value: 'expired', text: 'Expired' },
];

export const EMPTY_FILTERS = {
  search: '',
  regions: [],
  status: [],
  start_date: '',
  end_date: '',
};

export const EMPTY_POPUP = {
  id: null,
  name: '',
  heading: '',
  subheading: '',
  body: '',
  cta_label: '',
  cta_url: '',
  show_trusted_partner: true,
  regions: [...ALL_REGIONS],
  start_date: '',
  end_date: '',
  is_active: true,
  is_draft: false,
  image_desktop: null,
  image_mobile: null,
};

let latestFetchToken = 0;

function errorText(e, fallback) {
  if (typeof e === 'string') return e || fallback;
  return (e && e.message) || fallback;
}

export default {
  namespaced: true,

  state: () => ({
    popups: [],
    total: 0,
    regionBreakdown: [],
    page: 1,
    itemsPerPage: 10,
    filters: { ...EMPTY_FILTERS },
    excludeGfMembers: false,
    currentPopup: { ...EMPTY_POPUP },
    desktopImage: null,
    mobileImage: null,
    desktopImagePreview: '',
    mobileImagePreview: '',
    report: null,
    loading: false,
    saving: false,
    error: '',
  }),

  getters: {
    partnerPopups: (state) => state.popups,
    partnerPopupTotal: (state) => state.total,
    partnerPopupRegionBreakdown: (state) => state.regionBreakdown,
    previewDesktopImage: (state) => (
      state.desktopImagePreview || state.currentPopup.image_desktop || ''
    ),
    previewMobileImage: (state, getters) => (
      state.mobileImagePreview || state.currentPopup.image_mobile || getters.previewDesktopImage
    ),
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },
    setSaving(state, saving) {
      state.saving = saving;
    },
    setError(state, error) {
      state.error = error;
    },
    setPopups(state, {
      results, total, currentPage, regionBreakdown,
    }) {
      state.popups = results;
      state.total = total;
      state.page = currentPage;
      state.regionBreakdown = regionBreakdown;
    },
    setFilters(state, filters) {
      state.filters = { ...state.filters, ...filters };
      state.page = 1;
    },
    resetFilters(state) {
      state.filters = { ...EMPTY_FILTERS };
      state.page = 1;
    },
    setExcludeGfMembers(state, excludeGfMembers) {
      state.excludeGfMembers = excludeGfMembers;
    },
    setPage(state, page) {
      state.page = page;
    },
    setItemsPerPage(state, itemsPerPage) {
      state.itemsPerPage = itemsPerPage;
    },
    setCurrentPopup(state, popup) {
      state.currentPopup = Object.keys(EMPTY_POPUP).reduce((fields, key) => ({
        ...fields,
        [key]: popup[key] === undefined ? EMPTY_POPUP[key] : popup[key],
      }), {});
    },
    updateCurrentPopup(state, fields) {
      state.currentPopup = { ...state.currentPopup, ...fields };
    },
    setDesktopImage(state, file) {
      state.desktopImage = file;
      state.desktopImagePreview = file ? URL.createObjectURL(file) : '';
    },
    setMobileImage(state, file) {
      state.mobileImage = file;
      state.mobileImagePreview = file ? URL.createObjectURL(file) : '';
    },
    clearImages(state) {
      state.desktopImage = null;
      state.mobileImage = null;
      state.desktopImagePreview = '';
      state.mobileImagePreview = '';
    },
    setReport(state, report) {
      state.report = report;
    },
  },

  actions: {
    async fetchPopups({ state, commit }) {
      latestFetchToken += 1;
      const token = latestFetchToken;
      commit('setLoading', true);
      commit('setError', '');
      try {
        const result = await apiGetPartnerPopups({
          ...state.filters,
          exclude_gf_members: state.excludeGfMembers,
          page: state.page,
          itemsPerPage: state.itemsPerPage,
        });
        if (token !== latestFetchToken) return;
        commit('setPopups', {
          results: result.results || [],
          total: result.total || 0,
          currentPage: result.currentPage || 1,
          regionBreakdown: result.region_breakdown || [],
        });
      } catch (e) {
        if (token !== latestFetchToken) return;
        commit('setError', errorText(e, 'Could not load partner pop-ups.'));
      } finally {
        if (token === latestFetchToken) commit('setLoading', false);
      }
    },

    async applyFilters({ commit, dispatch }, filters) {
      commit('setFilters', filters);
      await dispatch('fetchPopups');
    },

    async clearFilters({ commit, dispatch }) {
      commit('resetFilters');
      await dispatch('fetchPopups');
    },

    startNewPopup({ commit }) {
      commit('setCurrentPopup', { ...EMPTY_POPUP });
      commit('clearImages');
      commit('setReport', null);
    },

    async loadPopup({ state, commit }, id) {
      commit('setLoading', true);
      commit('clearImages');
      try {
        const popup = await apiGetPartnerPopup(id, state.excludeGfMembers);
        commit('setCurrentPopup', popup);
        commit('setReport', popup);
      } catch (e) {
        commit('setError', errorText(e, 'Could not load that pop-up.'));
      } finally {
        commit('setLoading', false);
      }
    },

    async savePopup({ state, commit, dispatch }) {
      commit('setSaving', true);
      commit('setError', '');
      const images = { desktopImage: state.desktopImage, mobileImage: state.mobileImage };
      try {
        if (state.currentPopup.id) {
          await apiUpdatePartnerPopup(state.currentPopup, images);
        } else {
          await apiCreatePartnerPopup(state.currentPopup, images);
        }
        commit('clearImages');
        await dispatch('fetchPopups');
        return true;
      } catch (e) {
        commit('setError', errorText(e, 'Could not save that pop-up.'));
        return false;
      } finally {
        commit('setSaving', false);
      }
    },

    async deletePopup({ commit, dispatch }, id) {
      try {
        await apiDeletePartnerPopup(id);
        await dispatch('fetchPopups');
        return true;
      } catch (e) {
        commit('setError', errorText(e, 'Could not delete that pop-up.'));
        return false;
      }
    },
  },
};
